<template>
  <q-table
    v-model:pagination="pagination"
    :rows="dataWithoutHidden"
    :columns="columns"
    :loading="$q.loading.isActive || loading"
    flat
  >
    <template
      v-if="showAverageRow"
      #top-row="props"
    >
      <q-tr
        class="bg-grey-3 cursor-pointer"
        @click="onChangeAggregator"
      >
        <q-td
          v-for="col in props.cols"
          :key="col.name"
          class="text-bold"
          :class="col.__tdClass"
        >
          <div class="text-caption text-weight-thin insights-aggr-font">
            {{ getAggrText(col) }}
          </div>
          <div>{{ getAggr(col) }}</div>
        </q-td>
      </q-tr>
    </template>
    <template #body-cell-targetStatus="props">
      <q-td :props="props">
        <status-chip
          :target-status="props.row.targetStatus"
          :statuses="getTargetStatuses(props.row.workflow)"
        />
      </q-td>
    </template>
    <template #body-cell="props">
      <td
        :props="props"
      >
        <template v-if="props.col.useBadges">
          <q-badge
            v-for="item in props.value"
            :key="item.id"
            :label="item.label"
            class="q-mx-xs"
          />
        </template>
        <template v-else>
          {{ defaultTypeFormatting(props.value) }}
        </template>
      </td>
    </template>
  </q-table>
</template>

<script>
import StatusChip from 'components/Programs/StatusChip';
import {
  chain, find, get, head, isEqual, isFunction, reject,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { AGGREGATORS } from 'src/constants/chart';
import Insights from 'src/mixins/Insights';
import Targets from 'src/mixins/Targets';
import { programBehaviourData } from 'src/store/programBehaviourData';
import { programs } from 'src/store/programs';
import { workflows } from 'src/store/workflows';

export default {
  name: 'InsightsTable',

  components: { StatusChip },

  mixins: [Targets, Insights],

  props: {
    dataSeriesSettings: {
      type: Object,
      required: true,
    },
    series: {
      type: Array,
      required: true,
    },
    tableData: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    enableMinTrialsFilter: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      aggregator: 'average',

      pagination: {
        rowsPerPage: 20,
        sortBy: 'targetIntroductionDate',
        descending: true,
      },

      columns: [],
    };
  },

  computed: {
    ...mapState(programs, ['programs']),
    ...mapState(programBehaviourData, ['programBehaviourGroups', 'programBehaviourGroupItems']),
    ...mapState(workflows, ['workflowStatuses']),

    aggregators() {
      const aggregatorsArray = [
        AGGREGATORS.AVERAGE,
        AGGREGATORS.LOW,
        AGGREGATORS.HIGH,
        AGGREGATORS.SUM,
      ];

      if (this.dataSeriesSettings.aggregator === AGGREGATORS.WEIGHTED_AVERAGE) {
        aggregatorsArray.push(
          AGGREGATORS.WEIGHTED_AVERAGE,
        );
      }

      return aggregatorsArray;
    },

    tableColumns() {
      const isRatingScale = this.dataSeriesSettings?.dataSeriesPreset === 'spAverageRating';
      const isFocusedOnTarget = this.dataSeriesSettings?.groupByDataProperty === 'targetId'
        || get(this.dataSeriesSettings, 'postloadFilter.programs.targetId.length', 0) > 0;

      return chain(this.dSources)
        .find({ id: this.dataSeriesSettings.dataSource })
        .get('tableColumns', [])
        .filter((column) => {
          if (isRatingScale) {
            if (column.name === 'trials') {
              return isFocusedOnTarget;
            }
            return !['session', 'minTrials'].includes(column.name);
          }
          if (!this.enableMinTrialsFilter) {
            return !['minTrials', 'trials'].includes(column.name);
          }
          return true;
        })
        .value();
    },

    dataWithoutHidden() {
      return reject(this.tableData, { hidden: true });
    },

    showAverageRow() {
      if (this.dataSeriesSettings.dataSource === 'dsAssessmentData') {
        return false;
      }

      if (find(this.columns, { field: 'topography' })) {
        return false;
      }
      return !chain(this.columns)
        .reject((column) => (column.visibility || !column.field))
        .isEmpty()
        .value();
    }, // showAverageRow()

    dataSourceSettings() {
      return chain(this.dSources)
        .find({ id: this.dataSeriesSettings.dataSource })
        .value();
    },

    currentDataSeriesPresetLabel() {
      return chain(this.dataSourceSettings)
        .get('seriesPresets')
        .find({ id: this.dataSeriesSettings.dataSeriesPreset })
        .get('label')
        .value();
    }, // currentDataSeriesPresetLabel()

    behaviorIncidentProgram() {
      return chain(this.programs).filter({ type: 'behaviour' }).head().value();
    }, // behaviorIncidentProgram()

    topography() {
      return chain(this.programBehaviourGroups)
        .filter({ isDeleted: false })
        .map((group) => ({ id: group.id, label: group.label }))
        .value();
    },

  },

  watch: {
    dataSeriesSettings: {
      immediate: true,
      deep: true,
      async handler(newValue, oldValue) {
        if (!oldValue) {
          return;
        }
        if ((newValue.dataSource !== oldValue.dataSource)
            || (newValue.dataProperty !== oldValue.dataProperty)
            || (newValue.xAxisAggregateBy !== oldValue.xAxisAggregateBy)
            || (newValue.aggregator !== oldValue.aggregator)
            || (newValue.dataSeriesPreset !== oldValue.dataSeriesPreset)
            || !isEqual(newValue.postloadFilter, oldValue.postloadFilter)
            || (newValue.groupByDataProperty !== oldValue.groupByDataProperty)) {
          this.updateColumns();
          this.loadTableWorkflows();
        }

        if (newValue.xAxisOrderBy !== oldValue.xAxisOrderBy) {
          this.pagination.sortBy = get(newValue, 'xAxisOrderBy', null);
          this.pagination.descending = (newValue.xAxisType === 'date');
        }
      },
    },

  },

  async created() {
    await this.loadProgramBehaviourDataGroups({ programId: get(this.behaviorIncidentProgram, 'id'), loadItems: true });
  },

  methods: {
    ...mapActions(programBehaviourData, ['loadProgramBehaviourDataGroups']),
    ...mapActions(workflows, ['reloadWorkflows']),

    onChangeAggregator() {
      const aggrIndex = this.aggregators.indexOf(this.aggregator);
      this.aggregator = this.aggregators[(aggrIndex + 1) % this.aggregators.length];
    }, // onChangeAggregator()

    async updateColumns() {
      const columns = await this.getTableColumns(
        this.tableColumns,
        this.series,
        this.dataSeriesSettings,
        false,
        this.tableData,
      );
      this.columns = columns;

      this.aggregator = head(this.aggregators);
    }, // updateColumns()

    async loadTableWorkflows() {
      if (!find(this.tableColumns, { field: 'targetStatus' })) {
        return;
      }
      const workflowIds = chain(this.tableData)
        .map('workflow')
        .uniq()
        .compact()
        .value();

      return this.reloadWorkflows(workflowIds);
    }, // loadTableWorkflows()

    getTargetStatuses(workflowId) {
      return get(this.workflowStatuses, workflowId, null);
    }, // getTargetStatuses()

    getTopographyItems(topography, groupId) {
      return chain(topography)
        .get(`bg_${groupId}`)
        .map((item) => {
          return get(this.programBehaviourGroupItems, [groupId, item]);
        })
        .compact()
        .value();
    }, // getTopographyItems()

    getAggrText(column) {
      if (column.visibility || !column.field) {
        return '';
      }

      return this.$t(`insights.insightsTable.${this.aggregator}Label`);
    }, // getAggrText()

    defaultTypeFormatting(value, options = {
      numberCustomPrecision: 2,
    }, specifiedType = null) {
      const typeFormatting = {
        number: (val) => val.toFixed(options.numberCustomPrecision),
      };

      const valueType = specifiedType || typeof value;

      if (isFunction(typeFormatting[valueType])) {
        return typeFormatting[valueType](value);
      }

      return value;
    },

    getAggr(column) {
      if (column.visibility || !column.field) {
        return '';
      }
      let retVal;
      switch (this.aggregator) {
        case 'low':
          retVal = chain(this.tableData).minBy(column.field).get(column.field).value();
          break;
        case 'high':
          retVal = chain(this.tableData).maxBy(column.field).get(column.field).value();
          break;
        case 'sum':
          retVal = chain(this.tableData)
            .sumBy(column.field)
            .defaultTo(null)
            .value();
          break;
        case 'weightedAverage': {
          const weightedSum = chain(this.tableData)
            .reject((data) => (data[column.field] === null) || (data[column.field] === undefined))
            .map((data) => data[column.field] * data[`${column.field}_weight`])
            .sum()
            .value();

          const totalWeight = chain(this.tableData)
            .sumBy(`${column.field}_weight`)
            .value();

          retVal = (weightedSum && totalWeight) ? weightedSum / totalWeight : null;
          break;
        }
        case 'average':
        default:
          retVal = chain(this.tableData)
            .map(column.field)
            .reject((v) => (v === null) || (v === undefined))
            .mean()
            .defaultTo(null)
            .value();
      }

      // Use aggrFormat/format to override default formatting
      if (isFunction(column.aggrFormat)) {
        return column.aggrFormat(retVal, this.aggregator);
      }
      if (isFunction(column.format)) {
        return column.format(retVal);
      }

      return this.defaultTypeFormatting(retVal);
    }, // getAggr
  },
};
</script>
<style>
.insights-aggr-font {
  font-size:0.7em;
}

.insights-truncate-label {
    white-space: nowrap;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.insights-truncate-label:hover {
    text-overflow: clip;
    white-space: normal;
    word-break: break-all;
}

</style>
